/** Status/color helpers for the Consolidated EOS Reporting view. */
import { colorSuccess600, colorWarning200, colorError600 } from '@amzn/meridian-tokens/base/color';
import { ComparisonStatus } from '../constants';

export type EosStatus = 'green' | 'yellow' | 'red';

/** Server-computed ComparisonStatus → card/pill color. Clients must not recompute thresholds
 *  (see the model docs); undefined in → undefined out, so callers can fall back. */
export const comparisonStatusToEosStatus = (s?: ComparisonStatus): EosStatus | undefined => {
  switch (s) {
    case ComparisonStatus.AHEAD:
    case ComparisonStatus.ON_TRACK:
      return 'green';
    case ComparisonStatus.AT_RISK:
      return 'yellow';
    case ComparisonStatus.CRITICAL:
      return 'red';
    default:
      return undefined;
  }
};

/** EosStatus → Meridian colour token (for card accent bars). */
export const statusToAccentColor: Record<EosStatus, string> = {
  green: colorSuccess600,
  yellow: colorWarning200,
  red: colorError600,
};

/** EosStatus → Meridian Tag semantic type (for delta pills). */
export const statusToTagType: Record<EosStatus, 'success' | 'warning' | 'error'> = {
  green: 'success',
  yellow: 'warning',
  red: 'error',
};

/** KPI card color from % of target; non-finite (0 target) → green, not red. */
export const pctToStatus = (pct: number): EosStatus => {
  if (!Number.isFinite(pct)) return 'green';
  return pct >= 95 ? 'green' : pct >= 90 ? 'yellow' : 'red';
};

/** HC card color from the absolute headcount gap (|staffed - target|). */
export const hcGapToStatus = (staffed: number, target: number): EosStatus => {
  const gap = Math.abs(staffed - target);
  return gap <= 5 ? 'green' : gap <= 12 ? 'yellow' : 'red';
};

/** Delta-pill color from a Volume/Rate delta %; non-finite (0 target) → green, not red. */
export const deltaPctToStatus = (deltaPct: number): EosStatus => {
  if (!Number.isFinite(deltaPct)) return 'green';
  return deltaPct >= -5 ? 'green' : deltaPct >= -10 ? 'yellow' : 'red';
};

/** Delta-pill color for an HC row from the |delta| as a % of target. */
export const hcDeltaToStatus = (delta: number, target: number): EosStatus => {
  if (!target) return 'green';
  const pct = Math.abs((delta / target) * 100);
  return pct <= 5 ? 'green' : pct <= 10 ? 'yellow' : 'red';
};

/** Percent of target, rounded to `digits` decimals; em dash when target is 0. */
export const percentOf = (value: number, target: number, digits = 0): string =>
  target ? ((value / target) * 100).toFixed(digits) : '—';

/** Signed percentage delta of value vs target, 1 decimal, e.g. "+4.8%" / "-1.8%"; em dash when target is 0. */
export const signedDeltaPct = (value: number, target: number): string => {
  if (!target) return '—';
  const d = ((value - target) / target) * 100;
  return `${d >= 0 ? '+' : ''}${d.toFixed(1)}%`;
};

/** Signed absolute delta, e.g. "+0" / "-18". */
export const signedDelta = (value: number, target: number): string => {
  const d = value - target;
  return `${d >= 0 ? '+' : ''}${d}`;
};
